import React, { useState } from 'react';
import { Link } from 'react-router-dom';

// Shutter profile weights in kg per square meter (kg/m2) standard in Israel
const SHUTTER_TYPES = [
    { id: 'muktzaf43', name: 'שלב מוקצף 43 (אלומיניום רגיל)', weightPerSqm: 3.5 },
    { id: 'muktzaf55', name: 'שלב מוקצף 55', weightPerSqm: 4.0 },
    { id: 'plastic', name: 'שלב פלסטיק / PVC', weightPerSqm: 5.0 },
    { id: 'mashuch_normal', name: 'שלב משוך עיוור', weightPerSqm: 8.5 },
    { id: 'mashuch_or', name: 'שלב אור / משוך פתוח', weightPerSqm: 9.0 },
    { id: 'mashuch_heavy', name: 'שלב משוך כבד (חנייה/ביטחון)', weightPerSqm: 11.0 }
];

export default function ShutterWeightCalculator() {
    const [width, setWidth] = useState('');
    const [height, setHeight] = useState('');
    const [shutterType, setShutterType] = useState('mashuch_or');

    const W = parseFloat(width) || 0;
    const H = parseFloat(height) || 0;

    let totalWeight = 0;
    let squareMeters = 0;
    let recommendedMotorNM = 0;
    let motorRecommendationText = '';

    if (W > 0 && H > 0) {
        squareMeters = (W / 1000) * (H / 1000);
        const selectedShutter = SHUTTER_TYPES.find(s => s.id === shutterType);

        if (selectedShutter) {
            // Add 10% to weight calculation to account for friction and bottom profile
            totalWeight = (squareMeters * selectedShutter.weightPerSqm) * 1.1;

            // Determine Motor Size (NM) assuming standard 60mm octagonal tube
            // 1 NM typically lifts ~1.5 to 1.8 kg safely over time.
            if (totalWeight <= 14) recommendedMotorNM = 10;
            else if (totalWeight <= 25) recommendedMotorNM = 20;
            else if (totalWeight <= 35) recommendedMotorNM = 30;
            else if (totalWeight <= 45) recommendedMotorNM = 40;
            else if (totalWeight <= 60) recommendedMotorNM = 50;
            else if (totalWeight <= 85) recommendedMotorNM = 80;
            else recommendedMotorNM = 120; // Heavy duty

            motorRecommendationText = `מנוע ${recommendedMotorNM} ניוטון-מטר (NM)`;
        }
    }

    return (
        <div className="max-w-3xl mx-auto p-6 bg-white rounded-xl shadow-lg mt-10" dir="rtl">
            <Link to="/professionals" className="inline-flex items-center gap-2 text-gray-500 hover:text-blue-700 mb-4 transition-colors font-bold">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                חזרה למחשבונים
            </Link>
            <div className="flex items-center gap-4 mb-6 border-b pb-4">
                <img src="/assets/logo-circle.png" alt="Alum Nawatha" className="w-16 h-16 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                <h2 className="text-3xl font-bold text-gray-800">מחשבון תריסי גלילה והתאמת מנוע</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div>
                    <label className="block text-gray-700 font-bold mb-2">רוחב פתח (מ"מ):</label>
                    <input type="number" value={width} onChange={(e) => setWidth(e.target.value)} className="border-2 border-gray-300 p-3 rounded-lg w-full text-lg outline-none focus:border-blue-500" placeholder="לדוגמה: 1500" />
                </div>
                <div>
                    <label className="block text-gray-700 font-bold mb-2">גובה תריס מלא (מ"מ):</label>
                    <input type="number" value={height} onChange={(e) => setHeight(e.target.value)} className="border-2 border-gray-300 p-3 rounded-lg w-full text-lg outline-none focus:border-blue-500" placeholder="לדוגמה: 2100" />
                    <p className="text-xs text-gray-500 mt-1">* יש להזין גובה כולל גלילה בארגז</p>
                </div>
                <div>
                    <label className="block text-gray-700 font-bold mb-2">סוג שלב התריס:</label>
                    <select value={shutterType} onChange={(e) => setShutterType(e.target.value)} className="border-2 border-gray-300 p-3 rounded-lg w-full text-lg outline-none cursor-pointer bg-white focus:border-blue-500">
                        {SHUTTER_TYPES.map(s => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                    </select>
                </div>
            </div>

            {totalWeight > 0 && (
                <div className="bg-gray-50 border border-gray-200 p-6 rounded-lg">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-center">
                        <div className="p-4 border-l">
                            <p className="text-gray-600 mb-1">משקל התריס (כולל מקדם חיכוך):</p>
                            <div className="text-4xl font-mono font-bold text-gray-800">
                                {totalWeight.toFixed(1)} <span className="text-xl text-gray-500">ק"ג</span>
                            </div>
                        </div>
                        <div className="p-4">
                            <p className="text-gray-600 mb-1">מנוע מומלץ (Somfy / פלמט):</p>
                            <div className="text-3xl font-bold text-green-600 mt-2">
                                {motorRecommendationText}
                            </div>
                            {recommendedMotorNM >= 80 && (
                                <p className="text-sm text-red-500 mt-2 font-bold">שים לב: מנוע תעשייתי. ייתכן ונדרש צינור 70 מ"מ במקום 60 מ"מ.</p>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
